import { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import type { CreateContactRequest, ContactType } from '../../../types/contact';
import type { Organization } from '../../../types/organization';
import { MIN_SEARCH_LENGTH } from '../../../utils/searchConstants';
import { formatContactFullName } from '../../../utils/contactName';
import OrganizationAutocomplete from '../../organization/OrganizationAutocomplete';
import { isValidEmail } from '../../../utils/emailUtils';

interface CreateContactProps {
  open: boolean;
  companyId: number;
  defaultType?: ContactType;
  onClose: () => void;
  onSubmit: (data: CreateContactRequest) => Promise<void>;
}

export default function CreateContact({
  open,
  companyId,
  defaultType = 'CLIENT',
  onClose,
  onSubmit,
}: CreateContactProps) {
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [type, setType] = useState<ContactType>(defaultType);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleTransitionEnd = () => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const resetForm = () => {
    setLastName('');
    setFirstName('');
    setMiddleName('');
    setPhoneNumber('');
    setEmail('');
    setType(defaultType);
    setOrganization(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!lastName.trim() || !firstName.trim()) {
      setError('Прізвище та ім’я обов’язкові');
      return;
    }

    if (formatContactFullName({ lastName, firstName, middleName }).length < MIN_SEARCH_LENGTH) {
      setError(`Повне ім'я має містити щонайменше ${MIN_SEARCH_LENGTH} символів (потрібно для пошуку)`);
      return;
    }

    if (phoneNumber.trim() && phoneNumber.trim().length < MIN_SEARCH_LENGTH) {
      setError(`Номер телефону має містити щонайменше ${MIN_SEARCH_LENGTH} символів (потрібно для пошуку)`);
      return;
    }

    if (email.trim() && !isValidEmail(email)) {
      setError('Некоректний email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        companyId,
        lastName: lastName.trim(),
        firstName: firstName.trim(),
        middleName: middleName.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        email: email.trim() || undefined,
        type,
        organizationId: organization?.id,
      });

      // Reset form and close dialog on success
      resetForm();
      onClose();
    } catch (err) {
      console.error('Error creating contact:', err);
      setError('Не вдалося створити контакт. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          onEntered: handleTransitionEnd,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Створити новий контакт</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={inputRef}
            label="Прізвище"
            fullWidth
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setError(null);
            }}
            error={!!error}
            disabled={loading}
            required
            margin="normal"
          />
          <TextField
            label="Ім'я"
            fullWidth
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setError(null);
            }}
            error={!!error}
            helperText={error}
            disabled={loading}
            required
            margin="normal"
          />
          <TextField
            label="По батькові"
            fullWidth
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
            disabled={loading}
            margin="normal"
            helperText="Необов’язково"
          />
          <TextField
            label="Номер телефону"
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            disabled={loading}
            margin="normal"
            placeholder="+38 (0XX) XXX-XX-XX"
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            margin="normal"
            helperText="Необов’язково. Клієнта з каталогу буде знайдено за цим email"
          />
          <TextField
            select
            label="Тип"
            fullWidth
            value={type}
            onChange={(e) => setType(e.target.value as ContactType)}
            disabled={loading}
            margin="normal"
            required
          >
            <MenuItem value="CLIENT">Клієнт</MenuItem>
            <MenuItem value="PROVIDER">Постачальник</MenuItem>
            <MenuItem value="BOTH">Клієнт і постачальник</MenuItem>
          </TextField>
          <OrganizationAutocomplete
            companyId={companyId}
            value={organization}
            onChange={setOrganization}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Створення...' : 'Створити'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
